/**
 * 会话管理API服务 - 调用端口9002的会话管理+付费服务
 * 实现从旧API到新会话管理服务的平滑迁移
 */

// API配置
const CONVERSATION_API_BASE = process.env.REACT_APP_CONVERSATION_API_BASE || 'http://localhost:9002';

// 类型定义
export interface ConversationRequest {
  initial_message: string;
  user_id?: number | null;
  language?: string;
  metadata?: Record<string, any>;
}

export interface ContinueConversationRequest {
  user_message: string;
  language?: string;
}

export interface ConversationResponse {
  session_id: string;
  conversation_stage: 'free' | 'payment_pending' | 'paid' | 'analyzing' | 'analyzed' | 'expired';
  message: string;
  needs_clarification: boolean;
  can_proceed_to_payment: boolean;
  analysis_fee_jpy?: number;
  case_info?: Record<string, any>;
}

export interface ConversationListItem {
  session_id: string;
  conversation_stage: string;
  total_messages: number;
  case_title?: string;
  created_at: string;
  updated_at: string;
  expires_at?: string;
  is_analysis_paid: boolean;
}

export interface ConversationListResponse {
  conversations: ConversationListItem[];
  total_count: number;
  has_next: boolean;
}

export interface AnalysisReadyResponse {
  is_ready: boolean;
  case_info_complete: boolean;
  missing_info: string[];
  can_proceed_to_payment: boolean;
  analysis_fee_jpy: number;
}

export interface StreamingConversationRequest {
  query: string;
  conversation_id?: string | null;
  language?: string;
}

export interface CreatePaymentRequest {
  payment_method: string;
  analysis_type: string;
}

export interface PaymentResponse {
  payment_id: string;
  amount_jpy: number;
  payment_method: string;
  status: string;
  payment_url: string | null;
  paypay_payment_url: string | null;
  stripe_client_secret: string | null;
}

export interface PaymentConfig {
  stripe_public_key: string;
}

/**
 * 会话管理API类
 */
export class ConversationAPI {
  /**
   * 创建新会话
   */
  static async createConversation(request: ConversationRequest): Promise<ConversationResponse> {
    const response = await fetch(`${CONVERSATION_API_BASE}/api/conversations/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        initial_message: request.initial_message,
        user_id: request.user_id,
        language: request.language || 'ja',
        metadata: request.metadata
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`创建会话失败 (${response.status}): ${errorData.detail || response.statusText}`);
    }

    return await response.json();
  }

  /**
   * 继续会话
   */
  static async continueConversation(
    sessionId: string, 
    request: ContinueConversationRequest
  ): Promise<ConversationResponse> {
    const response = await fetch(`${CONVERSATION_API_BASE}/api/conversations/${sessionId}/continue`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_message: request.user_message,
        language: request.language || 'ja'
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`继续会话失败 (${response.status}): ${errorData.detail || response.statusText}`);
    }

    return await response.json();
  }

  /**
   * 获取用户会话列表
   */
  static async getUserConversations(page: number = 1, pageSize: number = 20): Promise<ConversationListResponse> {
    const response = await fetch(`${CONVERSATION_API_BASE}/api/conversations/list?page=${page}&page_size=${pageSize}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`获取会话列表失败 (${response.status}): ${errorData.detail || response.statusText}`);
    }

    return await response.json();
  }

  /**
   * 获取会话消息历史
   */
  static async getConversationMessages(sessionId: string): Promise<any> {
    const response = await fetch(`${CONVERSATION_API_BASE}/api/conversations/${sessionId}/messages`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`获取会话消息失败 (${response.status}): ${errorData.detail || response.statusText}`);
    }

    return await response.json();
  }

  /**
   * 检查分析准备状态
   */
  static async checkAnalysisReady(sessionId: string): Promise<AnalysisReadyResponse> {
    const response = await fetch(`${CONVERSATION_API_BASE}/api/conversations/${sessionId}/analysis-ready`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`检查分析准备状态失败 (${response.status}): ${errorData.detail || response.statusText}`);
    }

    return await response.json();
  }

  /**
   * 删除会话
   */
  static async deleteConversation(sessionId: string): Promise<void> {
    const response = await fetch(`${CONVERSATION_API_BASE}/api/conversations/${sessionId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`删除会话失败 (${response.status}): ${errorData.detail || response.statusText}`);
    }
  }

  /**
   * 流式对话（通过会话管理服务代理）
   */
  static async startStreamingConversation(request: StreamingConversationRequest): Promise<Response> {
    const response = await fetch(`${CONVERSATION_API_BASE}/api/conversations/streaming`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: request.query,
        conversation_id: request.conversation_id,
        language: request.language || 'ja'
      })
    });

    if (!response.ok) {
      throw new Error(`流式对话请求失败 (${response.status}): ${response.statusText}`);
    }

    return response;
  }

  /**
   * 处理流式响应数据（复用现有逻辑）
   */
  static async processStreamingResponse(
    response: Response,
    onStatus: (data: any) => void,
    onContent: (data: any) => void,
    onComplete: (data: any) => void,
    onError: (error: string) => void
  ): Promise<void> {
    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    
    if (!reader) {
      throw new Error('无法获取响应流');
    }

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data.trim()) {
              try {
                const streamData = JSON.parse(data);
                
                switch (streamData.type) {
                  case 'status':
                    onStatus(streamData.data);
                    break;
                  case 'content':
                    onContent(streamData.data);
                    break;
                  case 'complete':
                    onComplete(streamData.data);
                    return;
                  case 'error':
                    onError(streamData.data.message || streamData.error || '未知错误');
                    return;
                }
              } catch (parseError) {
                // 如果是纯文本错误消息，直接显示
                if (data.includes('error')) {
                  onError(data);
                }
              }
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * 健康检查
   */
  static async healthCheck(): Promise<any> {
    const response = await fetch(`${CONVERSATION_API_BASE}/api/conversations/health`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error(`健康检查失败 (${response.status}): ${response.statusText}`);
    }

    return await response.json();
  }
}

/**
 * 兼容性适配器 - 将新API响应转换为旧格式
 */
export class ConversationAdapter {
  /**
   * 将ConversationResponse转换为旧的澄清响应格式
   */
  static adaptToLegacyResponse(response: ConversationResponse): any {
    return {
      needs_clarification: response.needs_clarification,
      clarification_question: response.message,
      case_info: response.case_info || {},
      updated_case_info: response.case_info || {},
      analysis_capability: response.can_proceed_to_payment 
        ? '案情信息已收集完成，可以进行深度法律分析' 
        : 'より詳細な情報をご提供いただければ、より正確な法律分析をお提供いたします',
      completeness_score: response.can_proceed_to_payment ? 85 : 45,
      next_action: response.can_proceed_to_payment ? 'payment' : 'clarification',
      state: response.conversation_stage,
      conversation_id: response.session_id,
      session_id: response.session_id
    };
  }

  /**
   * 将会话列表转换为旧格式
   */
  static adaptConversationList(conversations: ConversationListItem[]): any[] {
    return conversations.map(conv => ({
      id: conv.session_id,
      title: conv.case_title || '法律咨询会话',
      timestamp: new Date(conv.updated_at),
      messageCount: conv.total_messages,
      status: conv.conversation_stage,
      isPaid: conv.is_analysis_paid
    }));
  }
}

/**
 * 支付API类
 */
export const PaymentAPI = {
  createPayment: async (data: {
    payment_method: string;
    analysis_type: string;
    session_id?: string;
    amount_jpy?: number;
  }) => {
    const response = await fetch(`${CONVERSATION_API_BASE}/api/payments/create-analysis-payment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '支付创建失败');
    }

    return response.json();
  },
  
  getPaymentStatus: async (paymentId: string) => {
    const response = await fetch(`${CONVERSATION_API_BASE}/api/payments/${paymentId}/status`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
      }
    });

    if (!response.ok) {
      throw new Error('获取支付状态失败');
    }

    return response.json();
  },
  
  getUserPayments: async (userId: number) => {
    const response = await fetch(`${CONVERSATION_API_BASE}/api/payments/user/${userId}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
      }
    });

    if (!response.ok) {
      throw new Error('获取用户支付记录失败');
    }

    return response.json();
  },
  
  getPaymentConfig: async () => {
    const response = await fetch(`${CONVERSATION_API_BASE}/api/payments/config`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
      }
    });

    if (!response.ok) {
      throw new Error('获取支付配置失败');
    }

    return response.json();
  },
  
  // 订阅管理相关方法
  getSubscriptionStatus: async () => {
    const response = await fetch(`${CONVERSATION_API_BASE}/api/subscriptions/status`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
      }
    });

    if (!response.ok) {
      throw new Error('获取订阅状态失败');
    }

    return response.json();
  },
  
  cancelSubscription: async (subscriptionId: string, options: { cancel_at_period_end: boolean }) => {
    const response = await fetch(`${CONVERSATION_API_BASE}/api/subscriptions/${subscriptionId}/cancel`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
      },
      body: JSON.stringify(options)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '取消订阅失败');
    }

    return response.json();
  },
  
  getSubscriptionPlans: async () => {
    const response = await fetch(`${CONVERSATION_API_BASE}/api/subscriptions/plans`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
      }
    });

    if (!response.ok) {
      throw new Error('获取订阅计划失败');
    }

    return response.json();
  }
}; 